import { useContext, useState } from 'react'
// import Button from '@components/ui/button'
import TextArea from '@components/ui/form/text-area'
import { useForm } from 'react-hook-form'
// import Text from '@components/ui/text'
import { Box, Button, Flex, Text } from '@chakra-ui/react'
import StarIcon from '@components/icons/star-icon'
import { ProductPageContext } from '@contexts/product-page'
import { useStore } from '@contexts/store'
import {
  addProductReview,
  editProductReview,
} from '@framework/products/manage-product-reviews'
import useSessionStorage from '@hooks/use-session-storage'
import cn from 'classnames'
import StarRatingComponent from 'react-star-rating-component'
import { toast } from 'react-toastify'
import { KeyedMutator } from 'swr'
import { useModalAction } from '../modal/modal.context'

interface ReviewFormProps {
  className?: string
  mutateProductReviews: KeyedMutator<any>
  product: any
}
interface ReviewFormValues {
  cookie: string
  message: string
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  product = {},
  className = '',
  mutateProductReviews,
}) => {
  const { storeData, user, analytics } = useStore()
  const { mutateAPIProductData } = useContext(ProductPageContext)
  const [productReviews, setProductReviews] = useSessionStorage('reviews', [])
  const [isNew, setIsNew] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const { openModal } = useModalAction()
  const previouslyRated = productReviews.find(
    (item: any) => item.product === product._id,
  )

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ReviewFormValues>({
    defaultValues: {
      cookie: '',
      message: previouslyRated?.comment || '',
    },
  })
  const [rating_custom_icon, set_rating_custom_icon] = useState<number>(
    previouslyRated?.rate || 3,
  )

  async function onSubmit(values: ReviewFormValues) {
    const payload = {
      fullName: user?.person?.fullname ?? '',
      email: user?.person?.email ?? '',
      comment: values.message,
      userId: user?.person?._id,
      rate: rating_custom_icon,
    }
    let review: any
    if (isEditing && previouslyRated) {
      const result = await editProductReview(
        storeData?._id,
        product._id,
        previouslyRated?.reviewId,
        user?.person?._id ?? '',
        payload,
      )
      review = result.review
    } else {
      const result = await addProductReview(
        storeData?._id,
        product._id,
        user?.person?._id ?? '',
        payload,
      )
      review = result.review
    }

    if (review?.success) {
      const newReview = {
        reviewId: review?.data?._id,
        product: product._id,
        rate: rating_custom_icon,
        comment: values.message,
      }
      productReviews.find((item: any) => item.product === product._id)
        ? setProductReviews(
            productReviews.map((item: any) =>
              item.product === product._id ? newReview : item,
            ),
          )
        : setProductReviews([...productReviews, newReview])

      analytics.track('Review submitted', {
        review: review?.data,
        product,
      })

      setIsNew(false)
      setIsEditing(false)

      toast.success('Thanks for your review!')
      mutateProductReviews()
      if (mutateAPIProductData) mutateAPIProductData()
    } else {
      toast.error(review?.msg ?? 'Failed to add review!')
    }
  }

  const onStarClickCustomIcon = (
    nextValue: number,
    prevValue: number,
    name: string,
  ) => {
    set_rating_custom_icon(nextValue)
  }

  const onEditReviewClick = () => {
    setIsEditing(true)
    analytics.track('Edit Review Clicked', {
      review: previouslyRated,
      product,
    })
  }

  const translateRating = (rate: number) => {
    switch (rate) {
      case 1:
        return 'Poor 😞'
      case 2:
        return 'Fair 😒'
      case 3:
        return 'Average 😐'
      case 4:
        return 'Good 😊'
      case 5:
        return 'Excellent 😍'
      default:
        return 'Average 😐'
    }
  }

  return (
    <Box
      className={cn(className)}
      bg="inputFieldOutline"
      borderRadius="6px"
      py="6"
      px="7"
    >
      {isNew || !previouslyRated ? (
        <>
          <Text
            as="h4"
            className="mb-2"
            color="primary.600"
            fontSize="18px"
            fontWeight="600"
          >
            Write your review
          </Text>
          <Text>Your personal details will not be published.</Text>
        </>
      ) : (
        <>
          <Text
            as="h4"
            className="mb-2"
            color="primary.600"
            fontSize="18px"
            fontWeight="600"
          >
            Edit your review
          </Text>
          <Box>
            You rated this product {previouslyRated?.rate} out of 5;
            <Flex
              direction="column"
              gap={4}
              mt={4}
              className="text-skin-base text-sm md:text-15px leading-none cursor-pointer ps-3"
            >
              <Box
                as="label"
                className="flex-shrink-0 block text-skin-base text-sm md:text-15px leading-none cursor-pointer pe-3"
              >
                <strong>Your rating:</strong>{' '}
                {translateRating(previouslyRated?.rate)}
              </Box>
              <Text sx={{ wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                {previouslyRated?.comment}
              </Text>
            </Flex>
          </Box>
        </>
      )}
      {isNew || isEditing || !previouslyRated ? (
        <>
          {user?.person?._id ? (
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="w-full mx-auto flex flex-col justify-center mt-5 lg:mt-7 xl:mt-9"
              noValidate
            >
              <Box className="flex flex-col">
                <Flex
                  direction={{ base: 'column', md: 'row' }}
                  gap={3}
                  className="pb-1.5"
                  align={{ base: 'start', md: 'center' }}
                >
                  <Text as="label" fontWeight="500" mr="2.5" fontSize="14px">
                    Your Rating
                  </Text>
                  <Flex
                    gap={3}
                    direction={{ base: 'column', md: 'row' }}
                    align={{ base: 'start', md: 'center' }}
                  >
                    <StarRatingComponent
                      name="app3"
                      starCount={5}
                      value={rating_custom_icon}
                      onStarClick={onStarClickCustomIcon}
                      starColor="#F3B81F"
                      emptyStarColor="#DFE6ED"
                      renderStarIcon={() => (
                        <StarIcon width="25px" height="25px" />
                      )}
                    />
                    <Text className="text-skin-base text-sm md:text-15px leading-none cursor-pointer ps-3">
                      {translateRating(rating_custom_icon)}
                    </Text>
                  </Flex>
                </Flex>

                <Text as="label" mb="2" fontSize="13px" opacity="0.7" mt="5">
                  Message
                </Text>
                <TextArea
                  variant="solid"
                  {...register('message', {
                    required: 'Please enter your comments',
                  })}
                  error={errors.message?.message}
                />
                <Box className="pt-1">
                  <Button
                    type="submit"
                    isLoading={isSubmitting}
                    isDisabled={isSubmitting}
                    colorScheme="primary"
                    rounded="full"
                    mt="5"
                  >
                    Submit Review
                  </Button>
                </Box>
              </Box>
            </form>
          ) : null}
        </>
      ) : (
        <Flex mt={4} justify={'space-between'}>
          <Button
            size={'sm'}
            onClick={onEditReviewClick}
            colorScheme="primary"
            rounded="full"
          >
            Edit Review
          </Button>
        </Flex>
      )}
    </Box>
  )
}

export default ReviewForm
